import * as React from 'react';
import styled, { css } from 'styled-components';
import getLayoutValues, { LayoutOptions } from './getLayoutValues';

interface Center extends LayoutOptions {
    /**
     * Either a string or a number.
     * It defines the gap between its direct children.
     *
     * As a number it will be transformed into px
     * gap={12} => gap: 12px;
     *
     * As a string you can pass your own unit
     * gap="1rem" => gap: 12rem;
     */
    gap?: number | string;
}

const centerStyles = `
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

const defaultStyles = `
    display: flex;
    align-items: stretch;
    justify-content: flex-start;
    text-align: left;
`;

const getChildrenGap = css`
    gap: ${({ gap }: { gap: Center['gap'] }) => (typeof gap === 'number' ? `${gap}px` : gap)};
`;

export const StyledCenter = styled.div<Center>`
    ${getLayoutValues(centerStyles, defaultStyles)}
`;

export const StyledVerticalCenter = styled.div<Center>`
    ${getLayoutValues(centerStyles, defaultStyles)}
    ${getChildrenGap}

    flex-direction: column;
`;

export const StyledHorizontalCenter = styled.div<Center>`
    ${getLayoutValues(centerStyles, defaultStyles)}
    ${getChildrenGap}
    
    flex-direction: row;
`;

const Center = (props: React.PropsWithChildren<Center>) => <StyledCenter {...props} />;
const VerticalCenter = (props: React.PropsWithChildren<Center>) => <StyledVerticalCenter {...props} />;
const HorizontalCenter = (props: React.PropsWithChildren<Center>) => <StyledHorizontalCenter {...props} />;

Center.Vertical = VerticalCenter;
Center.Horizontal = HorizontalCenter;

export default Center;
